import React, { useState, useRef, useEffect } from 'react';

const VoiceRecordingInput = ({ onSendMessage, isLoading, sttLang = 'en-US', voiceEnabled = true }) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isRecording, setIsRecording] = useState(false); // "listening" for dictation
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevels, setAudioLevels] = useState([0.3, 0.5, 0.7, 0.4, 0.6]);

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);
  const recognitionRef = useRef(null); // Web Speech API SpeechRecognition

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const maxHeight = 120;
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = newHeight >= maxHeight ? 'auto' : 'hidden';
      textarea.getBoundingClientRect();
    }
  }, [message]);

  // Initialize textarea height on mount
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch { /* ignore */ }
      }
    };
  }, []);

  const handleInputChange = (e) => {
    setMessage(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = async () => {
    // Don't send if AI is loading
    if (isLoading) return;

    if (message.trim() || attachments.length > 0) {
      // If mid-dictation, stop listening first
      if (isRecording) stopRecording();

      const messageData = {
        text: message.trim(),
        attachments: [...attachments]
      };

      onSendMessage(messageData);
      setMessage('');
      setAttachments([]);

      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setAudioLevels([0.3, 0.5, 0.7, 0.4, 0.6]);
  };

  // Speech-to-text dictation via the browser's Web Speech API (free, no key).
  // Transcribed words are appended to the message box; the user reviews & sends.
  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice input needs Chrome or Edge (Web Speech API not supported in this browser).');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = sttLang || 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognitionRef.current = recognition;

    // Text already in the box before dictation started
    let baseText = message ? message.trim() + ' ' : '';

    recognition.onresult = (event) => {
      let finalText = '';
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += transcript;
        else interim += transcript;
      }
      if (finalText) baseText = `${baseText}${finalText} `.replace(/\s+/g, ' ');
      setMessage((baseText + interim).replace(/\s+/g, ' ').trimStart());
    };

    recognition.onerror = (event) => {
      console.error('Dictation error:', event.error);
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        alert('Microphone permission denied. Please allow mic access and try again.');
      }
      setIsRecording(false);
      stopTimer();
    };

    recognition.onend = () => {
      setIsRecording(false);
      stopTimer();
    };

    try {
      recognition.start();
    } catch (error) {
      console.error('Could not start dictation:', error);
      return;
    }

    setIsRecording(true);
    setRecordingTime(0);

    // Timer + a lively (simulated) waveform while listening
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= 119) { stopRecording(); return 120; }
        return prev + 1;
      });
      setAudioLevels(prev => [...prev.slice(1), 0.3 + Math.random() * 0.6]);
    }, 500);
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
    }
    setIsRecording(false);
    stopTimer();
  };

  // Cancel = stop listening and discard the last (uncommitted) speech
  const cancelRecording = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* ignore */ }
    }
    setIsRecording(false);
    stopTimer();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const needsPreview = (type) =>
        type.startsWith('image/') || type.startsWith('video/') || type.startsWith('audio/');
      const newAttachments = files.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: needsPreview(file.type) ? URL.createObjectURL(file) : null,
        name: file.name,
        type: file.type,
        size: file.size
      }));
      setAttachments(prev => [...prev, ...newAttachments]);
    }
    e.target.value = '';
  };

  const removeAttachment = (id) => {
    setAttachments(prev => {
      const attachment = prev.find(a => a.id === id);
      if (attachment?.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return prev.filter(a => a.id !== id);
    });
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    if (fileType.startsWith('audio/')) return '🎵';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('sheet') || fileType.includes('excel')) return '📊';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="w-full">
      {/* Attachment Previews */}
      {attachments.length > 0 && (
        <div className="mb-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex flex-wrap gap-2">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="relative group flex items-center gap-2 bg-gray-50 dark:bg-gray-700 rounded-lg p-2 border border-gray-200 dark:border-gray-600"
              >
                {attachment.preview ? (
                  <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                    <img
                      src={attachment.preview}
                      alt={attachment.name}
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center text-lg">
                    {getFileIcon(attachment.type)}
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[120px]">
                    {attachment.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(attachment.size)}
                  </p>
                </div>
                
                <button
                  onClick={() => removeAttachment(attachment.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chat Input Container */}
      <div className={`
        relative flex items-end gap-2 p-3 bg-white dark:bg-gray-800 
        border border-gray-200 dark:border-gray-700 rounded-2xl 
        shadow-sm transition-all duration-200
        ${isFocused ? 'ring-2 ring-blue-500 dark:ring-blue-400 border-blue-500 dark:border-blue-400' : ''}
        ${isRecording ? 'ring-2 ring-red-500 dark:ring-red-400 border-red-500 dark:border-red-400' : ''}
        ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}
      `}>
        {/* File Attachment Button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isRecording || isLoading}
          className="flex-shrink-0 p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          title={isLoading ? "Please wait, AI is responding..." : "Attach files"}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        </button>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
        />

        {/* Delete Recording Button (Visible during recording) */}
        {isRecording && (
          <button
            onClick={cancelRecording}
            className="flex-shrink-0 p-2 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200"
            title="Cancel recording"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}

        {/* Auto-expanding Textarea or Recording Display */}
        <div className="flex-1 relative">
          {isRecording ? (
            <div className="flex items-center justify-between px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-xl">
              {/* Recording Duration */}
              <div className="text-red-600 dark:text-red-400 font-medium text-sm">
                {formatTime(recordingTime)}
              </div>
              
              {/* Animated Waveform Bars */}
              <div className="flex items-center gap-1">
                {audioLevels.map((level, index) => (
                  <div
                    key={index}
                    className="w-1 bg-red-500 dark:bg-red-400 rounded-full transition-all duration-200"
                    style={{
                      height: `${level * 20}px`,
                      animation: `pulse ${0.5 + index * 0.1}s infinite alternate`
                    }}
                  />
                ))}
              </div>
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              disabled={isLoading}
              placeholder={isLoading ? "AI is responding... Please wait" : "Type a message..."}
              rows="1"
              className={`
                w-full resize-none bg-transparent border-none outline-none
                text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400
                text-sm leading-5 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600
                scrollbar-track-transparent
                min-h-[20px] max-h-[120px]
                ${isLoading ? 'cursor-not-allowed' : ''}
              `}
              style={{
                height: 'auto',
                overflowY: 'hidden'
              }}
            />
          )}
        </div>

        {/* Voice Recording Button or Send Button */}
        {isRecording ? (
          <button
            onClick={stopRecording}
            className="flex-shrink-0 p-3 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            title="Stop recording"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12" rx="1"/>
            </svg>
          </button>
        ) : (
          <div className="flex items-center gap-1">
            {/* Voice Recording Button — only when voice mode is enabled for this user */}
            {voiceEnabled && (
              <button
                onClick={startRecording}
                disabled={isRecording || isLoading}
                className="flex-shrink-0 p-2 text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title={isLoading ? "Please wait, AI is responding..." : "Dictate — speak to type"}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </button>
            )}

            {/* Send Button */}
            <button
              onClick={handleSendMessage}
              disabled={isLoading || (!message.trim() && attachments.length === 0)}
              className={`
                flex-shrink-0 p-2 rounded-full transition-all duration-200
                ${isLoading
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : (message.trim() || attachments.length > 0
                    ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm hover:shadow-md transform hover:scale-105'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed')
                }
              `}
              title={isLoading ? "Please wait, AI is responding..." : "Send message"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Helper Text */}
      <div className={`mt-2 text-xs text-center ${isLoading ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
        {isLoading 
          ? "⏳ AI is responding... Please wait before sending another message"
          : isRecording 
            ? "Listening… speak now. Click stop when done, or cancel to discard."
            : "Press Enter to send, Shift+Enter for new line, or tap the mic to dictate"
        }
      </div>
    </div>
  );
};

export default VoiceRecordingInput;
